import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageCircle, UserX } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" });
const ago = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" });

export const useUnconfirmed = (token) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    fetch(`${API}/staff/unconfirmed-attendance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { bookings: [] }).then(d => setItems(d.bookings || [])).catch(() => {});
  }, [token]);
  useEffect(load, [load]);
  return [items, load];
};

export const NudgeButton = ({ item, token, onNudged }) => {
  const nudge = async () => {
    const phone = (item.patient_phone || "").replace(/[^0-9]/g, "");
    if (!phone) { toast.error("No phone number for this patient."); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(item.nudge_message)}`, "_blank");
    try {
      await fetch(`${API}/staff/bookings/${item.booking_id}/attendance-nudge`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      onNudged?.();
    } catch { /* WhatsApp already opened */ }
  };
  return (
    <div className="flex items-center gap-2">
      {item.last_nudged_at && <span className="text-[11px] text-stone-400" data-testid={`nudged-at-${item.booking_id}`}>Nudged {ago(item.last_nudged_at)}{item.nudge_count > 1 ? ` (${item.nudge_count}x)` : ""}</span>}
      <Button size="sm" onClick={nudge} className="h-8 rounded-lg text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`nudge-btn-${item.booking_id}`}>
        <MessageCircle className="w-3.5 h-3.5 mr-1" />{item.last_nudged_at ? "Nudge again" : "Nudge on WhatsApp"}
      </Button>
    </div>
  );
};

export const UnconfirmedAttendanceCard = ({ token }) => {
  const [items, load] = useUnconfirmed(token);
  if (!items.length) return null;
  return (
    <Card className="p-4 rounded-xl border-amber-200 bg-amber-50/40 space-y-3" data-testid="unconfirmed-attendance-card">
      <div className="flex items-center gap-2">
        <UserX className="w-[18px] h-[18px] text-amber-600" />
        <p className="text-sm font-bold text-amber-900 flex-1">{items.length} appointment{items.length > 1 ? "s" : ""} in the next 24 hours not confirmed by the patient</p>
        <Link to="/admin/consultations?filter=unconfirmed" className="text-xs text-amber-700 hover:underline" data-testid="unconfirmed-view-all">View in Consultations</Link>
      </div>
      <div className="divide-y divide-amber-100">
        {items.map(it => (
          <div key={it.booking_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2" data-testid={`unconfirmed-${it.booking_id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Link to={`/admin/patients/${it.patient_id}`} className="text-sm font-semibold text-stone-900 hover:text-[#FF7A00] truncate">{it.patient_name}</Link>
              <Badge variant="outline" className="text-[10px] capitalize">{(it.consultation_type || "").replace(/_/g, " ")}</Badge>
              <span className="text-xs text-stone-500">{fmt(it.appointment_datetime)} WAT</span>
            </div>
            <NudgeButton item={it} token={token} onNudged={load} />
          </div>
        ))}
      </div>
    </Card>
  );
};
